import { z } from 'zod'
import { Address, DecimalString, Handle, IsoTimestamp, MicroString, UsdString, Uuid } from '../primitives.js'
import { AgentKind, AgentStatus, AgentVisibility, BrainProvider, BrainTier, PerfRange, RiskProfile, Venue } from '../enums.js'
import { AgentSummary } from './common.js'
import { MAX_PERFORMANCE_FEE_BPS } from '../constants.js'
import { AgentAvatar } from './avatar.js'
import { Strategy, StrategyCadence, StrategyUniverse } from './strategy-base.js'
import { StrategyDocument } from './strategy.js'
import { LaunchTickRange, PositiveDecimalString } from './tokens.js'
import { ResearchPolicy } from './develop.js'

/** Owner setting (`agents.agents.transparency`, migration 0034); default everything public. */
export const AgentTransparency = z.object({
  /** Hide model prose (research narration, rationales) from public pages. */
  hidePlannerText: z.boolean(),
})
export type AgentTransparency = z.output<typeof AgentTransparency>
export const DEFAULT_TRANSPARENCY: AgentTransparency = { hidePlannerText: false }

export { Strategy, StrategyCadence, StrategyUniverse }

// ---------------------------------------------------------------------------
// Catalog

/** `provider/model` or `openrouter/<vendor>/<model>` (OpenRouter-routed brains). */
export const BrainId = z.string().regex(/^[a-z0-9-]+\/(?:[a-z0-9-]+\/)?[a-zA-Z0-9._-]+$/, 'expected provider/model or openrouter/vendor/model')
export type BrainId = z.output<typeof BrainId>

export const Brain = z.object({
  id: BrainId,
  provider: BrainProvider,
  model: z.string(),
  displayName: z.string(),
  tier: BrainTier,
  enabled: z.boolean(),
  sort: z.number().int(),
  /** Public active or paused agents on this brain (lane R7, W1-A5). The API always sends it; optional for the web stub. */
  agentsCount: z.number().int().nonnegative().optional(),
})
export type Brain = z.output<typeof Brain>

// ---------------------------------------------------------------------------
// Agent definition (agents.agents.voice, agents.agent_versions.strategy / model_policy)

export const AgentVoice = z.object({
  tone: z.string().min(1).max(200),
  style: z.string().min(1).max(500),
  catchphrases: z.array(z.string().min(1).max(200)).max(20),
})
export type AgentVoice = z.output<typeof AgentVoice>

/** `plan`: the runtime asks the planner for intents on top of the spec's (runtime `planner/`, docs/DEVIATIONS.md strategy-spec). */
export const ModelInterpretation = z.enum(['never', 'on_events', 'every_tick', 'plan'])
export type ModelInterpretation = z.output<typeof ModelInterpretation>

/**
 * The runtime's trigger for an agent-initiated token launch (docs/CONTRACTS.md "Agent-initiated launches"):
 * `agent.launch_token` runs on activation, after the first profitable fill, or only when asked (`manual`: the
 * owner or `POST /agent/token/launch`). The on-chain `LaunchPolicy` the creator set from their wallet is
 * authoritative: missing fields default to it (quote asset, supply, a start price inside its bounds), and
 * params outside it are refused before signing.
 */
export const LaunchModelPolicy = z.object({
  enabled: z.boolean(),
  when: z.enum(['on_activate', 'on_first_profit', 'manual']),
  name: z.string().min(1).max(40).optional(),
  symbol: z
    .string()
    .regex(/^[A-Z0-9]{2,11}$/, 'symbol must be 2-11 upper-case letters or digits')
    .optional(),
  /** Total supply in base units (18 decimals); default: the launch policy's supply. */
  supply: z.string().regex(/^[0-9]+$/).optional(),
  /** Allowlisted quote asset (zero address = native ETH); default: the launch policy's. */
  quoteAsset: Address.optional(),
  /** Quote units per 1 agent token; default: the geometric middle of the policy's price bounds. */
  startingPrice: PositiveDecimalString.optional(),
  tickRange: LaunchTickRange.optional(),
})
export type LaunchModelPolicy = z.output<typeof LaunchModelPolicy>

/**
 * Information tools of the agent kernel's registry (`@algoboo/agent-kernel` `defaultRegistry`, model-facing ones) a
 * plan-mode planner may call. The kernel checks this list against its registry in a test.
 */
export const KERNEL_MODEL_TOOL_IDS = ['market.lookup', 'memory.search', 'backtest.quick', 'wallets.top', 'market.trending', 'safety.check'] as const
export const ModelToolId = z.enum(KERNEL_MODEL_TOOL_IDS)
export type ModelToolId = z.output<typeof ModelToolId>
/** Cost class of a kernel tool (`@algoboo/agent-kernel` `ToolCostClass`; `read` 0.001 and `compute` 0.01 credits a call). */
export type ModelToolCostClass = 'read' | 'compute'

export interface ModelToolInfo {
  id: ModelToolId
  label: string
  description: string
  costClass: ModelToolCostClass
  /** Credits charged per call in micro-credits (`@algoboo/model-gateway` `TOOL_COST_MICRO`). */
  costMicro: number
  /** Calls allowed per tick (the registry's `capPerTick`). */
  capPerTick: number
}

/**
 * Display metadata of `KERNEL_MODEL_TOOL_IDS` for tool pickers: mirrors the kernel registry's
 * `costClass` and `capPerTick` (`packages/agent-kernel/src/tools/builtin.ts`).
 */
export const KERNEL_MODEL_TOOLS: Readonly<Record<ModelToolId, ModelToolInfo>> = {
  'market.lookup': { id: 'market.lookup', label: 'Market lookup', description: 'Feature rows for up to 10 tokens by symbol or address, also outside the universe.', costClass: 'read', costMicro: 1_000, capPerTick: 4 },
  'memory.search': { id: 'memory.search', label: 'Memory search', description: "The agent's own memory lines most relevant to a query.", costClass: 'read', costMicro: 1_000, capPerTick: 2 },
  'backtest.quick': { id: 'backtest.quick', label: 'Quick backtest', description: 'Backtest of the current v2 spec over recent hourly history.', costClass: 'compute', costMicro: 10_000, capPerTick: 1 },
  'wallets.top': { id: 'wallets.top', label: 'Top wallets', description: 'Top copyable wallets from analytics (pnl, win rate, hold time).', costClass: 'read', costMicro: 1_000, capPerTick: 1 },
  'market.trending': { id: 'market.trending', label: 'Trending tokens', description: 'The current trending tokens from analytics (score, rank, buyers, liquidity).', costClass: 'read', costMicro: 1_000, capPerTick: 1 },
  'safety.check': { id: 'safety.check', label: 'Safety check', description: 'Token safety verdicts (sell simulation, taxes, owner powers, LP lock).', costClass: 'compute', costMicro: 10_000, capPerTick: 64 },
}

/**
 * Canonical tool id for a stored name: the id itself, or its wire form (`memory_search`, the
 * provider-facing name the kernel sends) mapped back; null for anything else.
 */
export function canonicalModelToolId(name: unknown): ModelToolId | null {
  if (typeof name !== 'string') return null
  const id = name.trim().replace(/_/g, '.')
  return (KERNEL_MODEL_TOOL_IDS as readonly string[]).includes(id) ? (id as ModelToolId) : null
}

/** Stored `model_policy.tools` normalized: canonical ids, unknown names dropped, duplicates removed, order kept. */
export function normalizeModelToolIds(names: unknown): ModelToolId[] {
  if (!Array.isArray(names)) return []
  const out: ModelToolId[] = []
  for (const n of names) {
    const id = canonicalModelToolId(n)
    if (id && !out.includes(id)) out.push(id)
  }
  return out
}

/** `model_policy.tools`: distinct registry tool ids (default none: the planner gets no information tools). */
export const ModelTools = z
  .array(ModelToolId)
  .max(KERNEL_MODEL_TOOL_IDS.length)
  .refine((xs) => new Set(xs).size === xs.length, 'tools must be distinct')
export type ModelTools = z.output<typeof ModelTools>

export const ModelPolicy = z.object({
  brainId: BrainId,
  maxTokensPerTick: z.number().int().positive(),
  maxCallsPerTick: z.number().int().positive(),
  maxDelegationDepth: z.number().int().min(0).max(4),
  maxConcurrency: z.number().int().positive(),
  maxContextTokens: z.number().int().positive(),
  maxSpendMicroPerDay: z.number().int().nonnegative(),
  /** When the runtime asks the model to interpret a tick (default `never` = rules only). */
  interpretation: ModelInterpretation.optional(),
  /**
   * Plan mode delegation (runtime planner): which sub-roles the planner may call. Each
   * delegation is its own budgeted model call, bounded by `maxDelegationDepth` (default 1
   * when unset) and `maxCallsPerTick`. Default: both roles.
   */
  delegation: z
    .object({
      roles: z.array(z.enum(['research', 'risk_critic'])).max(2),
      /** Run the risk critic over every proposed buy even when the planner does not ask. */
      alwaysCritic: z.boolean().optional(),
    })
    .optional(),
  /** Agent-initiated token launch trigger (`agent.launch_token`); absent = never on its own. */
  launch: LaunchModelPolicy.optional(),
  /** Kernel information tools the plan-mode planner may call (`ModelToolId`); absent = none. */
  tools: ModelTools.optional(),
  /** The develop loop's research budget and cadence (docs/RUNTIME.md "Develop loop"); absent = the defaults. */
  research: ResearchPolicy.optional(),
})
export type ModelPolicy = z.output<typeof ModelPolicy>

/** Daily model spend cap the api stores when `plan`/`every_tick` is enabled without one: 500 credits. */
export const DEFAULT_MODEL_MAX_SPEND_MICRO_PER_DAY = 50_000_000
/** Interpretation modes that call a model on (almost) every tick and so need a daily cap. */
export const MODEL_DRIVEN_INTERPRETATIONS: readonly ModelInterpretation[] = ['plan', 'every_tick']

/**
 * Model policy on writes: `interpretation` defaults to `never` and is always
 * stored; `maxSpendMicroPerDay` may be omitted (the api stores
 * DEFAULT_MODEL_MAX_SPEND_MICRO_PER_DAY for `plan`/`every_tick`, else its rules-only default).
 */
export const ModelPolicyInput = ModelPolicy.extend({
  maxSpendMicroPerDay: z.number().int().nonnegative().optional(),
  interpretation: ModelInterpretation.default('never'),
})
export type ModelPolicyInput = z.input<typeof ModelPolicyInput>

/** PATCH /agents/:handle `modelPolicy`: fields merged into the current version's policy (a new version is recorded). */
export const ModelPolicyPatch = ModelPolicy.partial()
export type ModelPolicyPatch = z.input<typeof ModelPolicyPatch>

/** Performance fee in bps, 0..1000 (10 %), docs/ECONOMICS.md. */
export const PerformanceFeeBps = z.number().int().min(0).max(MAX_PERFORMANCE_FEE_BPS)

// ---------------------------------------------------------------------------
// Read models

export const AgentStats = z.object({
  followersCount: z.number().int().nonnegative(),
  subscribersCount: z.number().int().nonnegative(),
  holdersCount: z.number().int().nonnegative(),
  accountsCount: z.number().int().nonnegative(),
  /**
   * The trading figures are the platform's (lane BOO2: algo.boo keeps no book): null until the platform serves a
   * strategy's performance (docs/BACKLOG.md BOO2 "needs platform route"). Never 0 for unknown (ADR-0008).
   */
  aumUsd: UsdString.nullable(),
  pnl24hUsd: UsdString.nullable(),
  pnl7dUsd: UsdString.nullable(),
  pnl30dUsd: UsdString.nullable(),
  pnlAllUsd: UsdString.nullable(),
  winRate: DecimalString.nullable(),
  tradesCount: z.number().int().nonnegative().nullable(),
  lastActiveAt: IsoTimestamp.nullable(),
})
export type AgentStats = z.output<typeof AgentStats>

/** List/grid card. `pnlUsd` is for the requested range. */
export const AgentCard = z.object({
  id: Uuid,
  handle: Handle,
  name: z.string(),
  tagline: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  avatar: AgentAvatar.nullable(),
  kind: AgentKind,
  status: AgentStatus,
  brainId: BrainId.nullable(),
  riskProfile: RiskProfile.nullable(),
  range: PerfRange,
  /** The platform's figures, null until it serves a strategy's performance (lane BOO2). */
  pnlUsd: UsdString.nullable(),
  aumUsd: UsdString.nullable(),
  followersCount: z.number().int().nonnegative(),
  winRate: DecimalString.nullable(),
  tradesCount: z.number().int().nonnegative().nullable(),
  token: z.object({ address: Address.nullable(), symbol: z.string() }).nullable(),
  lastActiveAt: IsoTimestamp.nullable(),
  /** Where the agent trades (its venue of record, migration 0031); older payloads without it parse to `robinhood`. */
  venue: Venue.default('robinhood'),
  /** The creator (links `/u/:handle`); optional for older producers, `handle` null before the user picked one. */
  creator: z.object({ handle: Handle.nullable(), displayName: z.string() }).nullable().optional(),
})
export type AgentCard = z.output<typeof AgentCard>

/** Public strategy fields of one version. */
export const AgentVersionSummary = z.object({
  id: Uuid,
  version: z.number().int().positive(),
  brainId: BrainId.nullable(),
  /** The strategy in words and, once saved, the platform strategy it runs (`platform`). */
  strategy: StrategyDocument,
  changeNote: z.string().nullable(),
  createdAt: IsoTimestamp,
})
export type AgentVersionSummary = z.output<typeof AgentVersionSummary>

export const AgentTokenBadge = z.object({
  address: Address.nullable(),
  symbol: z.string(),
  name: z.string(),
  status: z.enum(['pending', 'live', 'halted']),
  holdersCount: z.number().int().nonnegative(),
})

/**
 * Whether the agent's model budget lets it think today (BACKLOG P5): `budget_exhausted` (no credits) or
 * `daily_cap` (its daily spend cap) after a `BUDGET_EXHAUSTED` refusal since 00:00 UTC, else `ok`. Rules-only
 * ticks and protective exits keep running either way.
 */
export const AgentThinking = z.enum(['ok', 'budget_exhausted', 'daily_cap'])
export type AgentThinking = z.output<typeof AgentThinking>

export const AgentProfile = z.object({
  id: Uuid,
  handle: Handle,
  name: z.string(),
  tagline: z.string().nullable(),
  bio: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  avatar: AgentAvatar.nullable(),
  voice: AgentVoice,
  kind: AgentKind,
  status: AgentStatus,
  visibility: AgentVisibility,
  /** Where the agent trades (migration 0031); its mandates are on accounts of the same venue. */
  venue: Venue.default('robinhood'),
  brainId: BrainId.nullable(),
  creator: z.object({ id: Uuid, handle: Handle.nullable(), displayName: z.string() }),
  currentVersion: AgentVersionSummary.nullable(),
  stats: AgentStats,
  token: AgentTokenBadge.nullable(),
  onchainAgentId: z.string().nullable(),
  /** Present when the request carries a session. */
  viewer: z.object({ following: z.boolean(), subscribed: z.boolean(), isOwner: z.boolean() }).nullable(),
  /** Performance fee on realized profit above the high-water mark, bps (0..MAX_PERFORMANCE_FEE_BPS). */
  performanceFeeBps: PerformanceFeeBps,
  activatedAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
  /** Model budget state today (`AgentThinking`); the API always sends it, `ok` for external agents. */
  thinking: AgentThinking.optional(),
  /** What the public timeline shows (migration 0034); the API always sends it. */
  transparency: AgentTransparency.optional(),
})
export type AgentProfile = z.output<typeof AgentProfile>

export const UsagePoint = z.object({
  at: IsoTimestamp,
  calls: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  chargedMicro: MicroString,
  sharedMicro: MicroString,
})
export const UsageSeries = z.object({
  agentId: Uuid,
  range: PerfRange,
  bucketS: z.number().int().positive(),
  points: z.array(UsagePoint),
  totals: z.object({
    calls: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    chargedMicro: MicroString,
    sharedMicro: MicroString,
  }),
})
export type UsageSeries = z.output<typeof UsageSeries>

// ---------------------------------------------------------------------------
// Requests

export const CreateAgent = z.object({
  handle: Handle,
  name: z.string().min(1).max(60),
  tagline: z.string().max(140).nullable(),
  bio: z.string().max(8000).nullable(),
  avatarUrl: z.url().optional(),
  avatar: AgentAvatar.nullable().optional(),
  voice: AgentVoice,
  brainId: BrainId,
  strategy: StrategyDocument,
  modelPolicy: ModelPolicyInput.optional(),
  riskProfile: RiskProfile,
  performanceFeeBps: PerformanceFeeBps.default(0),
  /** Default `robinhood`; a venue that is not live is refused 409 `VENUE_NOT_AVAILABLE`. */
  venue: Venue.default('robinhood'),
})
export type CreateAgent = z.input<typeof CreateAgent>

export const PatchAgent = z
  .object({
    name: z.string().min(1).max(60),
    tagline: z.string().max(140).nullable(),
    bio: z.string().max(8000).nullable(),
    avatarUrl: z.url().nullable(),
    avatar: AgentAvatar.nullable(),
    voice: AgentVoice,
    visibility: AgentVisibility,
    performanceFeeBps: PerformanceFeeBps,
    /** Merged into the current version's model policy; records a new version (same strategy). */
    modelPolicy: ModelPolicyPatch,
    /** Timeline transparency (owner setting; default everything public). */
    transparency: AgentTransparency,
  })
  .partial()
export type PatchAgent = z.input<typeof PatchAgent>

export const CreateAgentVersion = z.object({
  strategy: StrategyDocument,
  modelPolicy: ModelPolicyInput,
  changeNote: z.string().min(1).max(2000),
})
export type CreateAgentVersion = z.input<typeof CreateAgentVersion>

/** POST /agent/register (session): creates an external agent and returns its key once. */
export const RegisterExternalAgent = z.object({
  handle: Handle,
  name: z.string().min(1).max(60),
  tagline: z.string().max(140).nullable().optional(),
  bio: z.string().max(8000).nullable().optional(),
  avatarUrl: z.url().optional(),
  avatar: AgentAvatar.nullable().optional(),
  voice: AgentVoice,
  strategy: StrategyDocument,
  /** Default `robinhood`; a venue that is not live is refused 409 `VENUE_NOT_AVAILABLE`. */
  venue: Venue.default('robinhood'),
})
export type RegisterExternalAgent = z.input<typeof RegisterExternalAgent>

export const AgentKeyIssued = z.object({
  agent: AgentSummary,
  apiKey: z.string().regex(/^rhb_agent_[1-9A-HJ-NP-Za-km-z]+$/),
  prefix: z.string(),
})
export type AgentKeyIssued = z.output<typeof AgentKeyIssued>

export const HandleAvailability = z.object({
  available: z.boolean(),
  kind: z.enum(['user', 'agent']).nullable(),
})
export type HandleAvailability = z.output<typeof HandleAvailability>
